/**
 * @file EditableMeetingTitle.tsx
 * @brief The recorder page's meeting title heading, renamed in place.
 *
 * @details
 * DES 11-10-2026 ("is it not editable here", "haan editable karo"). Click the
 * title (or its pen) to edit; Enter or leaving the field saves, Esc cancels;
 * an empty title is not saved. Two lines at most, then "…" (the editor shows
 * it all). The page saves the
 * new name into the recording's draft and creates the meeting with it.
 *
 * @author DES
 * @date 11-10-2026
 */

import { capitalizeFirst } from "@/shared/utils/capitalizeFirst";
import { useEffect, useRef, useState } from "react";
import { PenLine } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import { isRTLText } from "@/shared/utils/formatters";

// Dimmed (DES 11-10-2026: "title ka colour dim karo"): the notes are the page's focus.
const HEADING = "text-2xl font-bold leading-tight tracking-tight text-foreground/70";

export function EditableMeetingTitle({
  title,
  onSave,
  disabled = false,
}: {
  title: string;
  onSave: (title: string) => void;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) setDraft(title);
  }, [title, editing]);

  useEffect(() => {
    const el = inputRef.current;
    if (!editing || !el) return;
    el.focus();
    el.select();
    el.style.height = "0px";
    el.style.height = `${el.scrollHeight}px`;
  }, [editing]);

  const finish = (save: boolean) => {
    setEditing(false);
    // First letter a capital (DES 12-10-2026).
    const next = capitalizeFirst(draft.replace(/\s*\n\s*/g, " ").trim());
    if (save && next && next !== title) onSave(next);
    else setDraft(title);
  };

  const dir = isRTLText(editing ? draft : title) ? "rtl" : "ltr";

  if (editing) {
    return (
      <textarea
        ref={inputRef}
        rows={1}
        dir={dir}
        value={draft}
        maxLength={200}
        aria-label="Meeting title"
        onChange={(e) => {
          setDraft(e.target.value);
          e.currentTarget.style.height = "0px";
          e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            finish(true);
          } else if (e.key === "Escape") {
            e.preventDefault();
            finish(false);
          }
        }}
        onBlur={() => finish(true)}
        className={cn(
          HEADING,
          "w-full max-w-[45rem] resize-none overflow-hidden border-0 border-b border-border bg-transparent p-0 pb-1 outline-none focus:border-brand",
        )}
      />
    );
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setEditing(true)}
      title="Rename this meeting"
      className="group flex max-w-[45rem] items-start gap-2 text-left disabled:cursor-default"
    >
      {/* At most two lines, then "…"; hover shows it whole (DES 11-10-2026:
          "top wala bhi two line main karo"). 720px wide at most, so a long
          title wraps instead of running across a wide page ("looks the
          same to me"). Editing shows all of it. */}
      <h1 dir={dir} title={title} className={cn(HEADING, "line-clamp-2 min-w-0 break-words")}>
        {title}
      </h1>
      {!disabled ? (
        <PenLine
          aria-hidden
          className="mt-1.5 h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        />
      ) : null}
    </button>
  );
}

export default EditableMeetingTitle;
